/**
 * Delete Button Component
 *
 * A centralized delete button with consistent styling used across
 * bulk actions and detail pages.
 *
 * Features:
 * - Outline style with red-600 border and text
 * - Hover: red-600 background with white text and icon
 * - Built-in TrashIcon with proper color transitions
 * - Loading state with spinner
 * - Consistent sizing
 *
 * Usage:
 * ```tsx
 * <DeleteButton onClick={handleClick} isLoading={isDeleting}>
 *   Delete
 * </DeleteButton>
 * ```
 */

"use client";

import { Button } from "@/components/ui/primitives/button";
import { Trash2 } from "lucide-react";
import { cn } from "@/lib/utils/general-utils";
import * as React from "react";

// =============================================================================
// TYPES
// =============================================================================

export interface DeleteButtonProps {
  /** Click handler */
  onClick: () => void;
  /** Whether the delete operation is in progress */
  isLoading?: boolean;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Button content (defaults to "Delete") */
  children?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Aria label for accessibility */
  ariaLabel?: string;
  /** Hide the icon */
  hideIcon?: boolean;
  /** Size variant */
  size?: "sm" | "md";
  /** Native title tooltip (e.g. disabled reason) */
  title?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function DeleteButton({
  onClick,
  isLoading = false,
  disabled = false,
  children = "Delete",
  className,
  ariaLabel = "Delete",
  hideIcon = false,
  size = "md",
  title,
}: Readonly<DeleteButtonProps>) {
  const sizeClasses = {
    sm: "px-2 py-1 text-xs",
    md: "px-3 py-1.5 text-sm",
  };

  return (
    <Button
      type="button"
      outline
      onClick={onClick}
      disabled={disabled || isLoading}
      aria-label={ariaLabel}
      title={title}
      className={cn(
        "group flex items-center gap-2",
        "!border-red-600 !text-status-error-text [&_svg]:!text-status-error-text",
        "hover:!border-red-600 hover:!text-white hover:!bg-red-600 hover:[&_svg]:!text-white",
        "transition-all duration-200",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        sizeClasses[size],
        className,
      )}
    >
      {isLoading ? (
        <svg
          className="animate-spin h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : (
        !hideIcon && (
          <Trash2 className="h-4 w-4 text-status-error-text group-hover:text-white transition-colors" />
        )
      )}
      <span>{children}</span>
    </Button>
  );
}
